/**
 * Debug Session Service
 * 
 * Manages the debug banner state for displaying session countdown and activity info.
 * Works with sessionRenewalService and sessionActivityService to provide debugging info.
 */

// Lazy imports to allow proper code splitting and avoid TDZ issues
let sessionRenewalServiceInstance = null
let sessionActivityServiceInstance = null

async function getSessionRenewalService() {
    if (!sessionRenewalServiceInstance) {
        const { sessionRenewalService } = await import('./sessionRenewalService.js')
        sessionRenewalServiceInstance = sessionRenewalService
    }
    return sessionRenewalServiceInstance
}

async function getSessionActivityService() {
    if (!sessionActivityServiceInstance) {
        const { sessionActivityService } = await import('./sessionActivityService.js')
        sessionActivityServiceInstance = sessionActivityService
    }
    return sessionActivityServiceInstance
}

export class DebugSessionService {
  constructor() {
    this.isDebugMode = false
    this.updateInterval = null
    this.sessionStorageKey = 'debug_mode_enabled'
    this.listeners = {
      'debug-mode-changed': [],
      'debug-info-updated': []
    }
    this.unsubscribeSessionRenewal = null
    this.renewalCount = 0
  }

  /**
   * Initialize debug service and restore state from session storage
   */
  async initialize() {
    // Restore debug mode state from session storage
    const savedState = sessionStorage.getItem(this.sessionStorageKey)
    if (savedState === 'true') {
      console.log('🐛 Restoring debug mode from session storage')
      await this.enableDebugMode()
    }

    // Subscribe to session renewal updates to get fresh data
    await this.subscribeToSessionUpdates()
  }

  /**
   * Subscribe to session renewal events for real-time updates
   */
  async subscribeToSessionUpdates() {
    const sessionRenewalService = await getSessionRenewalService()
    this.unsubscribeSessionRenewal = sessionRenewalService.on('renewal-success', () => {
      this.renewalCount++
      if (this.isDebugMode) {
        console.log('🐛 Session renewed - emitting fresh debug info')
        this.emit('debug-info-updated', this.getDebugInfo())
      }
    })
  }

  /**
   * Toggle debug mode
   */
  async toggleDebugMode() {
    if (this.isDebugMode) {
      await this.disableDebugMode()
    } else {
      await this.enableDebugMode()
    }
    return this.isDebugMode
  }

  /**
   * Enable debug mode
   */
  async enableDebugMode() {
    if (this.isDebugMode) {
      console.log('🐛 Debug mode already enabled')
      return
    }

    // Ensure dependent services are loaded before enabling
    await Promise.all([
      getSessionRenewalService(),
      getSessionActivityService()
    ])

    this.isDebugMode = true
    console.log('🐛 Debug mode enabled')

    // Persist state to session storage
    sessionStorage.setItem(this.sessionStorageKey, 'true')

    // Emit mode change first
    this.emit('debug-mode-changed', { enabled: true })

    // Emit immediate update so banner shows current info immediately
    this.emit('debug-info-updated', this.getDebugInfo())

    // Start periodic updates
    if (!this.updateInterval) {
      this.updateInterval = setInterval(() => {
        this.emit('debug-info-updated', this.getDebugInfo())
      }, 1000)
    }
  }

  /**
   * Disable debug mode
   */
  async disableDebugMode() {
    if (!this.isDebugMode) {
      console.log('🐛 Debug mode already disabled')
      return
    }

    this.isDebugMode = false
    console.log('🐛 Debug mode disabled')

    // Clear state from session storage
    sessionStorage.removeItem(this.sessionStorageKey)

    // Stop periodic updates
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
      this.updateInterval = null
    }

    this.emit('debug-mode-changed', { enabled: false })
  }

  /**
   * Get current debug information
   * @returns {Object} Debug info including session countdown, last activity, and idle time
   */
  getDebugInfo() {
    const sessionRenewalService = sessionRenewalServiceInstance
    const sessionActivityService = sessionActivityServiceInstance
    
    if (!sessionRenewalService || !sessionActivityService) {
      return {
        error: 'Services not initialized',
        sessionStatus: {},
        lastActivityTime: null,
        idleTimeMs: 0,
        renewalCount: this.renewalCount
      }
    }
    
    const sessionStatus = sessionRenewalService.getStatus()
    const lastActivityTimeMs = sessionActivityService.getLastActivityTime()
    const timeSinceLastActivity = sessionActivityService.getTimeSinceLastActivity()
    const config = sessionRenewalService.config
    
    // Log raw config for debugging
    if (!this._lastLoggedConfig || JSON.stringify(this._lastLoggedConfig) !== JSON.stringify(config)) {
      console.log('🐛 [DebugSessionService] Current config:', config)
      this._lastLoggedConfig = config
    }
    
    // Convert milliseconds to Date for formatting
    const lastActivityDate = lastActivityTimeMs ? new Date(lastActivityTimeMs) : null

    return {
      sessionStatus,
      lastActivityTime: lastActivityDate ? lastActivityDate.toISOString() : null,
      lastActivityTimeFormatted: lastActivityDate ? lastActivityDate.toLocaleTimeString() : 'N/A',
      idleTimeMs: timeSinceLastActivity,
      idleTimeFormatted: this.formatTime(Math.floor(timeSinceLastActivity / 1000)),
      countdownFormatted: this.formatCountdown(sessionStatus),
      renewalCount: this.renewalCount,
      config: config
    }
  }

  /**
   * Format countdown display
   */
  formatCountdown(sessionStatus) {
    if (!sessionStatus.initialized) {
      return 'Session not initialized'
    }
    if (sessionStatus.expired) {
      return 'Session expired'
    }
    const minutes = sessionStatus.minutesRemaining
    const seconds = sessionStatus.secondsRemaining % 60
    return `${minutes}m ${seconds}s`
  }

  /**
   * Format time as MM:SS
   */
  formatTime(seconds) {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  /**
   * Subscribe to debug events
   */
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback)
    } else {
      console.warn(`⚠️ Unknown debug event: ${event}`)
    }

    return () => {
      if (this.listeners[event]) {
        this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
      }
    }
  }

  /**
   * Emit debug event
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`❌ Error in debug ${event} listener:`, error)
        }
      })
    }
  }

  /**
   * Check if debug mode is active
   */
  isActive() {
    return this.isDebugMode
  }

  /**
   * Manually trigger session renewal (for testing)
   */
  async renewSession() {
    console.log('🐛 Manually triggering session renewal...')
    try {
      const sessionRenewalService = await getSessionRenewalService()
      await sessionRenewalService.renewToken()
      return { success: true }
    } catch (error) {
      console.error('🐛 Manual renewal failed:', error)
      return { success: false, error }
    }
  }

  /**
   * Accelerate session expiration to renewBeforeExpiryMinutes minutes (for testing)
   */
  async accelerateExpiration(timeOverrideMinutes = null) {
    const sessionRenewalService = await getSessionRenewalService()
    const sessionActivityService = await getSessionActivityService()
    const renewBeforeExpiryMinutes = sessionRenewalService.getStatus().renewalWindowMinutes;

    console.log('🐛 Accelerating session expiration to ' + renewBeforeExpiryMinutes + ' minutes...')
    
    // Stop monitoring to reset state before simulating expiration
    sessionRenewalService.stopMonitoring()
    
    const now = new Date()
    const futureTime = new Date(now.getTime() + (timeOverrideMinutes ? timeOverrideMinutes : renewBeforeExpiryMinutes) * 60000)
    sessionRenewalService.tokenExpiresAt = futureTime
    
    console.log('🐛 Session will now expire at:', futureTime.toISOString())
    
    // Pause activity tracking for an extended duration to prevent ANY activity detection
    // This needs to cover:
    // - The forceCheck execution
    // - The inactivity prompt display and any modal interactions
    // - Any background activity detection mechanisms
    // Using renewal window duration + buffer ensures it covers the full test scenario
    const pauseDurationMs = (renewBeforeExpiryMinutes * 60000) + 30000 // renewal window + 30s buffer
    sessionActivityService.pauseTrackingFor(pauseDurationMs)
    
    // Set idle time to simulate inactive user
    const pastTime = Date.now() - (renewBeforeExpiryMinutes * 60000 + 1000)
    sessionActivityService.lastActivityTime = pastTime
    localStorage.setItem('session_last_activity_time', pastTime.toString())
    console.log('🐛 User idle time set to ' + renewBeforeExpiryMinutes + ' minutes ago')
    console.log('🐛 Activity tracking paused for duration: ' + Math.floor(pauseDurationMs / 1000) + 's')
    
    // Reset rate limiting and force check
    sessionRenewalService.resetRenewalRateLimit()
    await sessionRenewalService.forceCheck()
    
    // Flag to skip the immediate check when restarting, to let the inactivity prompt show
    sessionRenewalService.skipNextInitialCheck = true
    
    // Schedule monitoring restart after a brief delay to let the current check flow complete
    // This avoids the immediate check in startMonitoring() from interfering with the test state
    setTimeout(() => {
      sessionRenewalService.startMonitoring()
    }, 1000)
    
    // Emit update to refresh display
    if (this.isDebugMode) {
      this.emit('debug-info-updated', this.getDebugInfo())
    }
  }

  /**
   * Cleanup
   */
  destroy() {
    this.disableDebugMode()
    if (this.unsubscribeSessionRenewal) {
      this.unsubscribeSessionRenewal()
    }
    Object.keys(this.listeners).forEach(key => {
      this.listeners[key] = []
    })
    console.log('🐛 Debug session service destroyed')
  }
}

// Export singleton with lazy getter pattern
let debugSessionServiceInstance = null

export function getDebugSessionService() {
    if (!debugSessionServiceInstance) {
        debugSessionServiceInstance = new DebugSessionService()
    }
    return debugSessionServiceInstance
}

// Lazy getter for backward compatibility with method binding
export const debugSessionService = new Proxy({}, {
    get(target, prop) {
        const instance = getDebugSessionService()
        const value = instance[prop]
        // Bind methods to preserve 'this' context when called through Proxy
        if (typeof value === 'function') {
            return value.bind(instance)
        }
        return value
    },
    set(target, prop, value) {
        const instance = getDebugSessionService()
        instance[prop] = value
        return true
    }
})
